import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Users, FileText, Clock, CheckSquare, Bell, Palmtree,
  Calendar, MessagesSquare, Award, HelpCircle, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration des accès rapides avec leurs appKey correspondants
const ALL_QUICK_LINKS = [
  { title: 'Planification', icon: Calendar, link: '/planning', color: 'text-blue-600', bgColor: 'bg-blue-50', appKey: 'planning' },
  { title: 'Suivi du temps', icon: Clock, link: '/time-tracking', color: 'text-emerald-600', bgColor: 'bg-emerald-50', appKey: 'time-tracking' },
  { title: 'Tâches', icon: CheckSquare, link: '/tasks', color: 'text-amber-600', bgColor: 'bg-amber-50', appKey: 'tasks' },
  { title: 'Chat', icon: MessagesSquare, link: '/chat', color: 'text-purple-600', bgColor: 'bg-purple-50', appKey: 'chat' },
  { title: 'Documents', icon: FileText, link: '/documents', color: 'text-red-600', bgColor: 'bg-red-50', appKey: 'documents' },
  { title: 'Reconnaissance', icon: Award, link: '/recognition', color: 'text-yellow-600', bgColor: 'bg-yellow-50', appKey: 'recognition' },
  { title: 'Employés', icon: Users, link: '/employees', color: 'text-indigo-600', bgColor: 'bg-indigo-50', appKey: 'employees', adminOnly: true },
];

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Employés', value: stats?.total_employees || 0, icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-100', borderColor: 'border-l-blue-500' },
    { title: 'Tâches en attente', value: stats?.pending_tasks || 0, icon: CheckSquare, color: 'text-amber-600', bgColor: 'bg-amber-100', borderColor: 'border-l-amber-500' },
    { title: 'Congés en attente', value: stats?.pending_leaves || 0, icon: Palmtree, color: 'text-emerald-600', bgColor: 'bg-emerald-100', borderColor: 'border-l-emerald-500' },
    { title: 'Tickets ouverts', value: stats?.open_tickets || 0, icon: HelpCircle, color: 'text-purple-600', bgColor: 'bg-purple-100', borderColor: 'border-l-purple-500' },
  ];

  // Filtrer les accès rapides selon les applications autorisées de l'utilisateur
  const quickLinks = ALL_QUICK_LINKS.filter(link => {
    // Filtrer les liens admin-only pour les non-admins
    if (link.adminOnly && !isAdmin) {
      return false;
    }
    // Les admins voient tout (visible_apps est null ou undefined)
    if (isAdmin || user?.visible_apps === null || user?.visible_apps === undefined) {
      return true;
    }
    // Les employés ne voient que les applications autorisées
    return user.visible_apps.includes(link.appKey);
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          Bonjour, {user?.first_name} !
        </h1>
        <p className="text-slate-500 mt-1">
          {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className={`border-l-4 ${stat.borderColor} hover:shadow-md transition-shadow`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <p className="text-4xl font-bold text-slate-800">{stat.value}</p>
                </div>
                <p className="text-sm text-slate-500 mt-3">{stat.title}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Accès rapide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.title}
                  to={link.link}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className={`p-3 rounded-xl ${link.bgColor}`}>
                    <Icon className={`w-6 h-6 ${link.color}`} />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{link.title}</span>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent News */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Dernières actualités
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_news?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_news.map((news) => (
                  <div key={news.id} className="p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary">{news.category}</Badge>
                      {news.pinned && <Badge className="bg-amber-100 text-amber-700">Épinglé</Badge>}
                    </div>
                    <h4 className="font-medium">{news.title}</h4>
                    <p className="text-sm text-slate-500 line-clamp-2">{news.content}</p>
                    <p className="text-xs text-slate-400 mt-1">{format(new Date(news.created_at), 'dd MMM yyyy', { locale: fr })}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">Aucune actualité</p>
            )}
            <Link to="/news" className="block text-center text-sm text-blue-600 hover:underline mt-4">
              Voir toutes les actualités →
            </Link>
          </CardContent>
        </Card>

        {/* Recent Recognitions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Reconnaissances récentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_recognitions?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_recognitions.map((rec) => (
                  <div key={rec.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-amber-100 text-amber-700">
                        {rec.recipient_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-medium">{rec.sender_name}</span>
                        <span className="text-slate-500"> a reconnu </span>
                        <span className="font-medium">{rec.recipient_name}</span>
                      </p>
                      <p className="text-sm text-slate-500 truncate italic">"{rec.message}"</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">Aucune reconnaissance</p>
            )}
            <Link to="/recognition" className="block text-center text-sm text-blue-600 hover:underline mt-4">
              Voir toutes les reconnaissances →
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Notifications summary */}
      {stats?.unread_notifications > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <Link to="/notifications" className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">
                Vous avez {stats.unread_notifications} notification{stats.unread_notifications > 1 ? 's' : ''} non lue{stats.unread_notifications > 1 ? 's' : ''}
              </span>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
